// AbortController isn't a JS-language feature, so core-js/plugin-legacy don't polyfill it —
// needed for axios (internally uses it for cancel/timeout) on old browsers like Firefox 52 (XP).
// Must load before anything else that might construct an AbortController.
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';

// IntersectionObserver is a Web API, not JS syntax, so core-js/plugin-legacy don't cover it either —
// Firefox 52 (XP) predates it (added in Firefox 55). Used by the study lists' infinite scroll.
import 'intersection-observer';

import { createRoot } from 'react-dom/client';

// third party
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

// project imports
import App from 'App';
import { store, persister } from 'store';
import * as serviceWorker from 'serviceWorker';
import reportWebVitals from 'reportWebVitals';
import { ConfigProvider } from 'contexts/ConfigContext';

// style + assets
import 'assets/scss/style.scss';

// yet-another-react-lightbox
import 'yet-another-react-lightbox/styles.css';

// google-fonts
// Preload critical fonts (for example, Roboto) to reduce FOUC.
// (In a real app, you might add a <link rel="preload" as="font" href="..." crossorigin="anonymous" /> tag in your HTML (or via a plugin) for each critical font.)
// (Below is a dummy example – replace with your actual font URLs if needed.)
// <link rel="preload" as="font" href="https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2" crossorigin="anonymous" />
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/700.css';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

// Firefox 52 (XP) only implements the legacy MediaQueryList.addListener/removeListener,
// not the modern addEventListener/removeEventListener — framer-motion's reduced-motion
// detection calls addEventListener directly on window.matchMedia(...) and crashes without this.
if (typeof MediaQueryList !== 'undefined') {
  const mqlProto = MediaQueryList.prototype as unknown as Record<string, unknown>;
  if (!mqlProto.addEventListener) {
    mqlProto.addEventListener = function (this: MediaQueryList & { addListener: (l: unknown) => void }, _type: string, listener: unknown) {
      this.addListener(listener as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    };
    mqlProto.removeEventListener = function (this: MediaQueryList & { removeListener: (l: unknown) => void }, _type: string, listener: unknown) {
      this.removeListener(listener as (this: MediaQueryList, ev: MediaQueryListEvent) => void);
    };
  }
}

// ==============================|| REACT DOM RENDER ||============================== //

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persister}>
      <ConfigProvider>
        <App />
      </ConfigProvider>
    </PersistGate>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
